import * as React from 'react';
import { useParams } from 'react-router-dom';
import { Button, Loading } from 'vet-bones/design-system/components';
import { alphaSort } from 'vet-bones/utils';

import { useAppDispatch } from 'src/ui/app/hooks';
import { DataTable, DataTableColumn } from 'src/ui/components/DataTable';
import {
  ORGANIZATION_ENDPOINT_TABLE_ADD,
  ORGANIZATION_ENDPOINT_TABLE_COLUMN_SORT,
  ORGANIZATION_ENDPOINT_TABLE_SEARCH,
} from 'src/ui/constants/testIds';
import { OrganizationEndpointRow } from 'src/ui/containers/endpoints/OrganizationEndpointRow';
import { getEndpointAuthType } from 'src/ui/containers/endpoints/utils/getAuthType';
import { DialogType, openedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  Connection,
  useListOrganizationConnectionsQuery,
} from 'src/ui/graph/types';
import * as copy from 'src/ui/templates/copy';

const COLUMNS: Array<DataTableColumn<Connection>> = [
  {
    key: 'name',
    className: 'organization-table-row-endpoint-name',
    header: copy.components.organization.endpointTable.columns.name,
    sortByAlpha: (item: Connection) => item.name,
    sortTestId: ORGANIZATION_ENDPOINT_TABLE_COLUMN_SORT('name'),
  },
  {
    key: 'url',
    className: 'organization-table-row-endpoint-url',
    header: copy.components.organization.endpointTable.columns.url,
    sortByAlpha: (item: Connection) => item.endpoint,
    sortTestId: ORGANIZATION_ENDPOINT_TABLE_COLUMN_SORT('url'),
  },
  {
    key: 'auth',
    className: 'organization-table-row-endpoint-auth',
    header: copy.components.organization.endpointTable.columns.auth,
    sortByAlpha: (item: Connection) => getEndpointAuthType(item),
    sortTestId: ORGANIZATION_ENDPOINT_TABLE_COLUMN_SORT('auth'),
  },
  {
    key: 'action',
    className: 'data-table-row-actions',
    header: copy.components.organization.endpointTable.columns.action,
  },
];

export const OrganizationEndpointTable: React.VFC = () => {
  const dispatch = useAppDispatch();
  const { org } = useParams<{ org: string }>();

  const {
    data: connectionsData,
    isLoading,
  } = useListOrganizationConnectionsQuery(graphQLClient, { org_domain: org });

  const endpointItems = React.useMemo(() => {
    const items: Connection[] = [];
    connectionsData?.listConnections?.forEach((conn) => {
      if (!conn) {
        return;
      }
      items.push(conn as Connection);
    });
    return items.sort(alphaSort((item) => item.name));
  }, [connectionsData]);

  const searchFilter = (item: Connection, query: string): boolean => {
    const trimmed = query.trim().toLowerCase();
    return (
      item.name.toLowerCase().includes(trimmed) ||
      item.endpoint.toLowerCase().includes(trimmed)
    );
  };

  const renderRow = (conn: Connection) => (
    <OrganizationEndpointRow connection={conn} />
  );

  const handleAdd = () => {
    dispatch(openedDialog({ type: DialogType.ADD_MY_ORGANIZATION_ENDPOINT }));
  };

  if (isLoading) {
    return (
      <div className="organization-dashboard-loading">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <DataTable
      className="organization-table"
      columns={COLUMNS}
      data={endpointItems}
      getRowKey={(conn) => conn.id}
      renderRow={renderRow}
      searchActions={
        <Button
          onClick={handleAdd}
          size="md"
          testId={ORGANIZATION_ENDPOINT_TABLE_ADD}
          variant="tertiary-accent"
        >
          {copy.components.organization.endpointTable.add}
        </Button>
      }
      searchFilter={searchFilter}
      searchInputTestId={ORGANIZATION_ENDPOINT_TABLE_SEARCH}
      searchPlaceholder={copy.components.organization.endpointTable.search}
      title={copy.components.organization.endpointTable.title(
        endpointItems.length
      )}
    />
  );
};
